import routeConfigsV2 from '../../config/route_v2.config'

/**
 * 动态页面的 path -> access 映射表（用于 isNewLayout 时补充 access）
 * key: 页面路径（包含 id）
 * value: 对应的 access 权限名
 * 来源: product-architecture.md 二、动态页面
 */
export const dynamicPageAccessMap: Record<string, string> = {
  // ===== 工作台 =====
  // 告警大屏
  '/workbench/6690db299ed071e51721831e': 'alarmScreen',

  // ===== 实时风险监控 =====
  // 店铺实时告警
  '/riskMonitor/66922f2822ab809971103a55': 'shopRealtimeAlarm',
  // 平台实时告警
  '/riskMonitor/66922f2822ab809971103a4f': 'platformRealtimeAlarm',
  // 全局实时告警
  '/riskMonitor/66922f2822ab809971103a49': 'globalRealtimeAlarm',
  // 实时告警报表
  '/riskMonitor/6691f6d522ab809971103a64b': 'realtimeAlarmReport',

  // ===== 人工质检 =====
  // 人工质检诊断报告
  '/qcManage/check/68a691a6ad2af9f81792f636': 'manualQcDiagnosticReport', // 淘宝的人工质检诊断报告路径
  '/qcManage/check/68a692b3ad2af9f81792f758': 'manualQcDiagnosticReport', // 其他平台的人工质检诊断报告路径

  // ===== 质检分析 =====
  // 质检诊断报告
  '/qcManage/check/68a691a6ad2af9f81792f639': 'qcDiagnosticReport',
  // 质检明细报表
  '/qcManage/check/67de4ee9ac92a95fb638f316': 'qcDetailReport',

  // ===== 质检报表 =====
  // 全局质检汇总表
  '/reportCenter/qcReport/69018eb8e48628355d7a3a60': 'globalQcSummary',
  // 全局质检标准检出表
  '/reportCenter/qcReport/69128b08be2024d466e4befb': 'globalQcStandardCheck',
  // 全局人工质检表
  '/reportCenter/qcReport/6909f4f7e48628355d7a53ff': 'globalManualQcReport',

  // ===== 客服评价满意度 =====
  '/reportCenter/67d2461ce63366f0474b20b0': 'csSatisfaction',
  '/reportCenter/6692264d22ab8099711037a3': 'csSatisfaction',

  // ===== 分析洞察Agent =====
  '/reportCenter/aiInsight': 'aiInsight',

  // ===== 质检效果监控 =====
  '/system/effectMonitor/697086af895d8a4fdd0faf3f': 'qcEffectMonitorPage',
}

/**
 * 递归遍历路由配置，收集 access 和 path 的映射关系
 * 一个 access 可能对应多个 path（如 csSatisfaction 对应京东和淘宝两个平台）
 */
function traverseRoutes(routes: any[], result: Map<string, string[]>) {
  routes.forEach((route) => {
    if (route.access && route.path) {
      const existing = result.get(route.access) || []
      result.set(route.access, [...existing, route.path])
    }
    if (route.routes) {
      traverseRoutes(route.routes, result)
    }
  })
}

/**
 * 从 route_v2.config.ts 动态获取 access -> paths 映射
 */
let accessPathsMap: Map<string, string[]> | null = null

function getAccessPathsMap(): Map<string, string[]> {
  if (!accessPathsMap) {
    accessPathsMap = new Map<string, string[]>()
    traverseRoutes(routeConfigsV2, accessPathsMap)
  }
  return accessPathsMap
}

/**
 * 在 isNewLayout 模式下，根据 access 名称查找对应的所有 path
 * 优先从 route_v2.config.ts 动态获取，找不到再从动态页面映射表查
 * @returns 路径数组，可能有多个（如 csSatisfaction 对应京东和淘宝两个平台）
 */
export function findPathsByAccess(accessName: string): string[] {
  const paths: string[] = []

  // 优先从 route_v2 动态获取
  const map = getAccessPathsMap()
  if (map.has(accessName)) {
    paths.push(...map.get(accessName)!)
  }

  // 再从动态页面映射表查
  const allDynamicPaths = Object.keys(dynamicPageAccessMap)
  for (const p of allDynamicPaths) {
    if (dynamicPageAccessMap[p] === accessName && !paths.includes(p)) {
      paths.push(p)
    }
  }

  return paths
}

/**
 * 根据 path 查找对应的 access
 * 优先从 route_v2.config.ts 动态获取，找不到再从动态页面映射表查
 * @returns access 名称，未找到返回 null
 */
export function findAccessByPath(path: string): string | null {
  // 优先从 route_v2 动态获取（遍历 access -> paths 的反向查找）
  const map = getAccessPathsMap()
  for (const [access, paths] of map.entries()) {
    if (paths.includes(path)) {
      return access
    }
  }

  // 再从动态页面映射表查
  if (dynamicPageAccessMap[path]) {
    return dynamicPageAccessMap[path]
  }

  return null
}
